import React from "react";

import { CheckIcon } from "@heroicons/react/outline";

import Heading from "components/heading";
import HeroSection from "components/hero-section";

const sections = [
  {
    title: "Origination Software",
    description:
      "Full origination software (LOS) and TPO origination — credit checks, pricing engine, stipulations, document generation, e-signatures, and homeowner-facing application flows. The origination infrastructure every bank, credit union, capital provider, solar lender, and TPO financier needs.",
    features: [
      "Branded application flow (your product, your identity)",
      "Custom pricing engine — built to your specs, with rebates and holdbacks capabilities",
      "Credit pull and qualification engine",
      "Stipulation management and document collection",
      "E-signature with full audit trail",
      "Homeowner portal and communication",
      "Multi-language support (English/Spanish)",
    ],
  },
  {
    title: "Operational Backend",
    description:
      "The administrative infrastructure that keeps a financing operation running — queues, statuses, assignments, escalations, and workflow orchestration.",
    features: [
      "Admin dashboards with role-based access",
      "Deal pipeline and status management",
      "Change order processing and reconciliation",
      "In-home sales support and installer tools",
      "Training and onboarding programs",
      "SOC 2 compliant with live support",
    ],
  },
  {
    title: "Risk & Fraud Management",
    description:
      "Protecting your capital from bad actors, bad data, and bad installs — before they hit your portfolio.",
    features: [
      "Audit trails on every deal action",
      "Contractor trend analysis",
      "Savings underwriting and geospatial detection",
      "License validation and e-sign tracking",
      "Title checks",
    ],
    roadmap: ["Deal integrity scoring", "AI-powered welcome calls", "ID verification"],
  },
  {
    title: "Security & Compliance",
    description:
      "Enterprise-grade security and compliance infrastructure — so your institution doesn't have to build it from scratch.",
    features: [
      "SOC 2 Type II certified",
      "Trust dashboard with real-time status",
      "Penetration testing program",
      "Cyber insurance coverage",
      "Vendor security reviews",
    ],
    roadmap: ["ISO 27001 / PCI compliance"],
  },
  {
    title: "Data & Reporting",
    description:
      "Portfolio intelligence and market analytics — the data layer institutional investors expect from their operating partners.",
    features: [
      "Market comparisons and benchmarking",
      "Installer performance tracking",
      "Utility rate and incentive mapping",
      "Milestone and pipeline analytics",
      "FICO distribution reports",
      "Forecasting and alert systems",
      "Capital markets data package",
    ],
  },
  {
    title: "Project Management & Monitoring",
    description: "From permit to PTO — full lifecycle tracking of every project in your solar portfolio.",
    features: [
      "Milestone-based project tracking",
      "Automated status updates and notifications",
      "Document and photo management",
      "Inspection coordination",
      "Installer accountability scoring",
      "PTO timeline analysis",
    ],
    roadmap: [
      "Required document collection from installers at each milestone",
      "Lender milestone review and approval workflows",
    ],
  },
  {
    title: "API & Integration Infrastructure",
    description:
      "Enerflo as your API hub — connecting your systems to the field without building custom integrations from scratch.",
    features: [
      "Open API with full documentation",
      "Webhook event system",
      "CRM and proposal tool integrations",
      "Custom data mapping and transformations",
      "White-label and co-branded options",
      "Dedicated integration engineering support",
    ],
  },
  {
    title: "Installer OS & Network",
    description:
      "When you launch with Enerflo, your product is immediately available to our nationwide installer network — already active, already trained on the platform. Enerflo handles all platform training and can host your financing-specific materials, integrate your program into our knowledge base and support chatbot, and drive rapid adoption while reducing the common onboarding issues financiers typically have to staff for themselves.",
    features: [
      "Immediate access to nationwide installer network on launch",
      "Platform training handled by Enerflo — financier materials hosted and delivered",
      "Financier knowledge integrated into Enerflo support chatbot and knowledge base",
      "Full CRM and lead management",
      "Proposal generation and native e-signatures",
      "Multi-lender marketplace (your product + others)",
      "Mobile-optimized for field teams",
      "Available free of charge to your installer network",
    ],
  },
];

const SectionBlock = ({
  index,
  title,
  description,
  features,
  roadmap,
}: typeof sections[number] & { index: number }) => {
  const hasRoadmapItems = !!roadmap?.length;
  return (
    <li className="p-3 lg:p-4 bg-gray-100 rounded-xl space-y-4 text-left">
      <header className="space-y-4">
        <div className="flex gap-4 items-center">
          <div className="bg-primary/20 text-primary border border-primary rounded-lg w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
            {index + 1}
          </div>
          <Heading tag="h5" size="h6" className="text-navy">
            {title}
          </Heading>
        </div>
        <div>
          <p className="text-gray-700 text-sm">{description}</p>
        </div>
      </header>
      <details className="space-y-4" open={index === 0}>
        <summary className="text-primary uppercase text-sm font-semibold w-fit">Features & Capabilities</summary>
        <div>
          {hasRoadmapItems && <strong className="text-navy uppercase text-sm inline-block mb-3">Live Now</strong>}
          <ul className="space-y-4">
            {features.map(item => (
              <li key={item} className="flex gap-2">
                <div className="inline-block p-0.5 h-5 w-5 rounded-full shadow-md bg-primary text-white shrink-0">
                  <CheckIcon />
                </div>
                <p className="text-gray-800">{item}</p>
              </li>
            ))}
          </ul>
          {hasRoadmapItems && (
            <>
              <strong className="text-navy uppercase text-sm inline-block mt-5 mb-3">Roadmap</strong>
              <ul className="space-y-4">
                {roadmap.map(item => (
                  <li key={item} className="flex gap-2">
                    <div className="inline-block p-1 rounded-full border border-primary bg-white h-5 w-5 shrink-0" />
                    <p className="text-gray-800">{item}</p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </details>
    </li>
  );
};

const title = "Solar Financing Operating System -- 8 Categories Included";

const OperatingSystemCategories = () => (
  <HeroSection title={title} className="flex-col gap-x-6 gap-y-10 md:flex-row md:space-x-0 items-start">
    <HeroSection.Content className="text-center">
      <header className="mb-12">
        <HeroSection.Tagline className="text-primary mb-4">{title}</HeroSection.Tagline>
        <Heading tag="h2" className="mb-6">
          <span>Solar Loan Origination Software, TPO Tools & More.</span>
          <br />
          <span className="text-primary inline-block mt-2">White-Labeled as Your Brand.</span>
        </Heading>
        <p className="text-lg text-gray-600 max-w-4xl mx-auto">
          Enerflo provides the full infrastructure stack — solar loan origination (LOS), TPO financing tools,
          compliance, risk monitoring, project tracking, and more — configured around your program and delivered
          entirely under your brand. All origination runs through Enerflo’s platform, so your installers and homeowners
          only ever see you. Choose the capabilities that fit your program, and where you have existing systems you want
          to keep, Enerflo integrates — so you’re never forced to replace what’s already working.
        </p>
      </header>

      <ul className="space-y-6 max-w-3xl mx-auto">
        {sections.map((feature, index) => (
          <SectionBlock key={feature.title} {...feature} index={index} />
        ))}
      </ul>
    </HeroSection.Content>
  </HeroSection>
);

export default OperatingSystemCategories;
